"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { LogIn, ShoppingCart, Sparkles, UserPlus } from "lucide-react";
import { IframeFullscreenModal } from "./iframe-fullscreen-modal";
import { DetailBackButton } from "./detail-back-button";
import { useShell } from "./page-shell";
import { prefersModalIframeFullscreen } from "../lib/prefers-modal-iframe-fullscreen";
import { track } from "@carpybara/analytics/client";

export type GateCtaIcon = "shopping-cart" | "log-in" | "user-plus";

export interface GateCta {
  href: string;
  label: string;
  icon: GateCtaIcon;
  variant: "primary" | "secondary";
}

export interface RouteGatePreviewCopy {
  kicker: string;
  title: string;
  body: string;
  details: string[];
  hint: string;
  ctas: GateCta[];
}

interface RouteGatePreviewProps {
  /** URL of the embedded walkthrough video iframe. */
  videoSrc: string;
  /** Tailwind gradient classes applied to the video tone overlay. */
  toneClassName: string;
  copy: RouteGatePreviewCopy;
  analytics?: {
    feature: string;
    tier: "anonymous" | "free" | "pro" | "ultra";
  };
}

const ICONS: Record<GateCtaIcon, typeof ShoppingCart> = {
  "shopping-cart": ShoppingCart,
  "log-in": LogIn,
  "user-plus": UserPlus,
};

export function RouteGatePreview({
  videoSrc,
  toneClassName,
  copy,
  analytics,
}: RouteGatePreviewProps) {
  const router = useRouter();
  const { t } = useShell();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const fullscreenIframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeFullscreenFallback, setIframeFullscreenFallback] =
    useState(false);
  const [iframeFullscreenModal, setIframeFullscreenModal] = useState(false);
  const [preferModalFullscreen, setPreferModalFullscreen] = useState(false);
  // Reserve for the Stage's playback bar below the 16:9 canvas — undershoot
  // it and the embed's fit-scale goes height-bound, rendering the video
  // narrower than the iframe. 70 matches the current bar; the embed posts
  // its measured height to self-correct.
  const [controlsPx, setControlsPx] = useState(70);
  const trackedPaywallRef = useRef<string | null>(null);

  useEffect(() => {
    if (!analytics) return;
    const key = `${analytics.feature}:${analytics.tier}`;
    if (trackedPaywallRef.current === key) return;
    trackedPaywallRef.current = key;
    track("paywall_viewed", analytics);
  }, [analytics]);

  useEffect(() => {
    setIframeFullscreenFallback(false);
    setIframeFullscreenModal(false);
  }, [videoSrc]);

  useEffect(() => {
    setPreferModalFullscreen(prefersModalIframeFullscreen());
  }, []);

  useEffect(() => {
    if (!iframeFullscreenFallback) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIframeFullscreenFallback(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [iframeFullscreenFallback]);

  useEffect(() => {
    const iframe = iframeRef.current;
    const postFullscreenState = () => {
      const payload = {
        type: "carpybara-video-fullscreen-state",
        fullscreen:
          Boolean(document.fullscreenElement) ||
          iframeFullscreenFallback ||
          iframeFullscreenModal,
      };
      iframe?.contentWindow?.postMessage(payload, "*");
      fullscreenIframeRef.current?.contentWindow?.postMessage(payload, "*");
    };
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: unknown; height?: unknown } | null;
      if (!data) return;

      // Stage reports its measured playback-bar height; grow the iframe so
      // the 16:9 canvas keeps full width.
      if (data.type === "carpybara-video-controls-height") {
        const fromOwnFrame =
          event.source === iframe?.contentWindow ||
          event.source === fullscreenIframeRef.current?.contentWindow;
        if (fromOwnFrame && typeof data.height === "number" &&
          Number.isFinite(data.height)) {
          setControlsPx(Math.min(160, Math.max(44, Math.ceil(data.height))));
        }
        return;
      }

      const activeWindow = iframeFullscreenModal
        ? fullscreenIframeRef.current?.contentWindow
        : iframe?.contentWindow;
      if (event.source !== activeWindow) return;
      if (data.type !== "carpybara-video-fullscreen-toggle") return;

      if (iframeFullscreenModal) {
        setIframeFullscreenModal(false);
        return;
      }

      if (iframeFullscreenFallback) {
        setIframeFullscreenFallback(false);
        return;
      }

      if (document.fullscreenElement) {
        if (typeof document.exitFullscreen === "function") {
          void document.exitFullscreen();
        }
        return;
      }

      if (preferModalFullscreen) {
        setIframeFullscreenModal(true);
        return;
      }

      if (iframe && typeof iframe.requestFullscreen === "function") {
        void iframe.requestFullscreen().catch(() => {
          if (preferModalFullscreen) {
            setIframeFullscreenModal(true);
            return;
          }
          setIframeFullscreenFallback(true);
        });
        return;
      }

      setIframeFullscreenFallback(true);
    };

    window.addEventListener("message", onMessage);
    document.addEventListener("fullscreenchange", postFullscreenState);
    postFullscreenState();
    return () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("fullscreenchange", postFullscreenState);
    };
  }, [
    iframeFullscreenFallback,
    iframeFullscreenModal,
    preferModalFullscreen,
    videoSrc,
  ]);

  const handleBack = useCallback(() => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
      return;
    }

    router.push("/");
  }, [router]);

  return (
    <main className="mx-auto w-full px-4 pb-10 pt-3 sm:pb-14 sm:pt-4 lg:pb-16 lg:pt-5">
      <div className="overflow-hidden rounded-3xl border border-(--border) bg-(--bg-card) shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-(--border) px-4 py-2.5 sm:px-5 sm:py-3">
          <DetailBackButton onClick={handleBack} className="mr-1">
            {t("common.back")}
          </DetailBackButton>
          <span className="inline-flex items-center gap-2 rounded-full border border-(--accent)/25 bg-(--accent-bg) px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-(--accent-text)">
            <Sparkles className="h-3 w-3" aria-hidden />
            {copy.kicker}
          </span>
        </div>

        <div className="p-5 sm:p-6 lg:p-7">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(300px,0.55fr)] lg:items-end">
            <div>
              <h1 className="max-w-3xl text-2xl font-bold leading-tight text-(--text-primary) sm:text-3xl lg:text-4xl">
                {copy.title}
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-(--text-muted) sm:text-base">
                {copy.body}
              </p>
            </div>

            <ul className="grid gap-2.5 rounded-2xl border border-(--border) bg-(--bg-secondary)/70 p-4">
              {copy.details.map((detail) => (
                <li
                  key={detail}
                  className="flex gap-3 text-sm leading-5 text-(--text-secondary)"
                >
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-(--accent)" />
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 border-t border-(--border) p-5 sm:p-6">
          <p className="max-w-xl text-center text-sm text-(--text-muted)">
            {copy.hint}
          </p>
          <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
            {copy.ctas.map((cta) => {
              const Icon = ICONS[cta.icon];
              const className =
                cta.variant === "primary"
                  ? "inline-flex items-center justify-center gap-2 rounded-full bg-(--accent) px-5 py-2.5 text-sm font-semibold text-(--accent-foreground) shadow-sm transition hover:opacity-90"
                  : "inline-flex items-center justify-center gap-2 rounded-full border border-(--border) bg-(--bg-secondary) px-5 py-2.5 text-sm font-semibold text-(--text-primary) transition hover:bg-(--bg-card)";
              return (
                <Link key={cta.href + cta.label} href={cta.href} className={className}>
                  <Icon className="h-4 w-4" aria-hidden />
                  {cta.label}
                </Link>
              );
            })}
          </div>
        </div>

        <div
          className="relative overflow-hidden border-t border-(--border) bg-(--bg-secondary) p-2 sm:p-3 dark:bg-black"
          style={{ containerType: "inline-size" }}
        >
          <div
            className={`absolute inset-0 bg-linear-to-br ${toneClassName}`}
            aria-hidden
          />
          <div className="absolute inset-x-0 top-0 h-24 bg-linear-to-b from-white/10 to-transparent opacity-40" />
          <iframe
            ref={iframeRef}
            title={copy.title}
            className={
              iframeFullscreenFallback
                ? "fixed inset-0 z-[120] h-[100dvh] w-screen rounded-none border-0 bg-black"
                : "relative z-10 w-full rounded-2xl border border-(--border) bg-(--bg-primary) shadow-lg shadow-black/10 dark:border-white/10 dark:bg-black dark:shadow-2xl dark:shadow-black/30"
            }
            style={
              iframeFullscreenFallback
                ? undefined
                : { height: `calc((100cqw * 9 / 16) + ${controlsPx}px)` }
            }
            src={videoSrc}
            allow="autoplay; fullscreen; picture-in-picture"
            loading="lazy"
            // First-party /how-it-works embed: allow-same-origin so it can read
            // its own sessionStorage + load same-origin assets (otherwise the
            // sandbox gives it an opaque origin and those throw SecurityErrors).
            sandbox="allow-scripts allow-same-origin"
          />
          <IframeFullscreenModal
            open={iframeFullscreenModal}
            onClose={() => setIframeFullscreenModal(false)}
            backdropLabel="Close"
            ref={fullscreenIframeRef}
            iframeKey={`${videoSrc}-fullscreen`}
            title={`${copy.title} fullscreen`}
            src={videoSrc}
          />
        </div>
      </div>
    </main>
  );
}
